import { z } from 'zod';

const withdrawAddressSchema = ({ regex, minAmount, maxAmount }, t) =>
  z.object({
    address: z
      .string({
        message: t('auth.required_field'),
      })
      .regex(new RegExp(regex), t('wallet.withdraw.error.card_number_format')),
    amount: z
      .number()
      .min(minAmount, 'Amount is too low')
      .max(maxAmount, 'Amount is too high'),
    name: z.string().optional(),
  });

const createSchema = (fields, schemaDefinition) => {
  const schema = { amount: schemaDefinition.amount };

  for (const field of fields) {
    if (schemaDefinition[field]) {
      schema[field] = schemaDefinition[field];
    }
  }

  return z.object(schema);
};

const bankCardSchema = (t, minAmount, maxAmount, fields) =>
  createSchema(fields, {
    card: z
      .string({ message: t('auth.required_field') })
      .regex(
        /^(\d{4} \d{4} \d{4} \d{4})$/,
        t('wallet.withdraw.error.card_number_format')
      )
      .refine((val) => val.replace(/\s+/g, '').length === 16, {
        message: t('wallet.withdraw.error.card_number_length'),
      }),
    amount: z.number().min(minAmount).max(maxAmount),
  });

const depositFiatSchema = (minAmount, maxAmount) =>
  z.object({
    amount: z.number().min(minAmount).max(maxAmount),
  });

const personalInfoSchema = (t) =>
  z.object({
    fname: z
      .string({ message: t('auth.required_field') })
      .min(2, t('wallet.fiat.input_fields.fname.error_min_length'))
      .regex(/^[A-Za-z]+$/, t('wallet.fiat.input_fields.fname.error_invalid')),
    lname: z
      .string({ message: t('auth.required_field') })
      .min(2, t('wallet.fiat.input_fields.lname.error_min_length'))
      .regex(/^[A-Za-z]+$/, t('wallet.fiat.input_fields.lname.error_invalid')),
    birthday: z
      .string({ message: t('auth.required_field') })
      .regex(
        /^\d{4}-\d{2}-\d{2}$/,
        t('wallet.fiat.input_fields.birthday.error_format')
      ),
    country: z
      .string({ message: t('auth.required_field') })
      .min(2, t('wallet.fiat.input_fields.birthday.error_invalid')),
    city: z
      .string({ message: t('auth.required_field') })
      .min(2, t('wallet.fiat.input_fields.city.error_min_length')),
    street: z
      .string({ message: t('auth.required_field') })
      .min(2, t('wallet.fiat.input_fields.street.error_min_length')),
    zip: z
      .string({ message: t('auth.required_field') })
      .regex(/^\d{4,7}$/, t('wallet.fiat.input_fields.zip.error_format')),
    phone: z
      .string({ message: t('auth.required_field') })
      .regex(/^\+?\d{7,15}$/, t('wallet.fiat.input_fields.phone.error_format')),
    email: z
      .string({ message: t('auth.required_field') })
      .email(t('auth.login.email.error_invalid')),
  });

const cardSchema = (t) =>
  z.object({
    card: z
      .string({ message: t('auth.required_field') })
      .regex(
        /^(\d{4}[-\s.]?\d{4}[-\s.]?\d{4}[-\s.]?\d{4}|\d{16})$/,
        t('wallet.withdraw.error.card_number_length')
      )
      .transform((val) => val.replace(/[^\d]/g, ''))
      .refine((val) => val.length === 16, {
        message: t('wallet.withdraw.error.card_number_length'),
      }),

    expireMonth: z.string({ message: t('auth.required_field') }).refine(
      (val) => {
        // Объединенная проверка формата и диапазона
        return /^\d{2}$/.test(val) && parseInt(val) >= 1 && parseInt(val) <= 12;
      },
      {
        message: t('wallet.fiat.input_fields.expire.error_invalid'),
      }
    ),

    expireYear: z.string({ message: t('auth.required_field') }).refine(
      (val) => {
        const currentYear = new Date().getFullYear() % 100;
        const currentYearFull = new Date().getFullYear();
        const century = Math.floor(currentYearFull / 100) * 100;

        // Проверка формата
        if (!/^\d{2}$/.test(val)) return false;

        const yearValue = parseInt(val);
        const fullYear = century + yearValue;

        // Проверка что год не в прошлом
        return fullYear >= currentYearFull;
      },
      {
        message: t('wallet.fiat.input_fields.expire.error_invalid'),
      }
    ),

    cvv: z
      .string({ message: t('auth.required_field') })
      .transform((val) => val.replace(/[^\d]/g, ''))
      .refine((val) => val.length === 3, {
        message: t('wallet.fiat.input_fields.cvv.error_format'),
      }),
  });

const holderCardSchema = (t) =>
  cardSchema(t).extend({
    holder: z
      .string({ message: t('auth.required_field') })
      .min(2, { message: t('auth.required_field') })
      .transform((val) => val.trim())
      .refine((val) => /^[a-zA-Z\s'-]+$/.test(val), {
        message: t('wallet.fiat.input_fields.fname.error_invalid'),
      }),
  });

const fieldValidators = (t) => ({
  iban: z
    .string({ message: t('auth.required_field') })
    .refine((val) => {
      const cleanVal = val.replace(/\s/g, '');
      return /^[A-Z]{2}[0-9A-Z]{2,}$/.test(cleanVal.toUpperCase());
    }, t('wallet.withdraw.error.iban_format'))
    .refine((val) => {
      const cleanVal = val.replace(/\s/g, '');
      return cleanVal.length >= 15 && cleanVal.length <= 34;
    }, t('wallet.withdraw.error.iban_length')),

  name: z
    .string({ message: t('auth.required_field') })
    .min(2, t('wallet.withdraw.error.full_name_length')),

  bank: z.string({ message: t('auth.required_field') }),

  tin: z
    .string({ message: t('auth.required_field') })
    .refine((val) => {
      const cleanVal = val.replace(/[\s-]/g, '');
      return /^\d+$/.test(cleanVal);
    }, t('wallet.withdraw.error.tin_format'))
    .refine((val) => {
      const cleanVal = val.replace(/[\s-]/g, '');
      return cleanVal.length >= 9 && cleanVal.length <= 12;
    }, t('wallet.withdraw.error.tin_length')),

  card: z
    .string({ message: t('auth.required_field') })
    .regex(
      /^(\d{4} \d{4} \d{4} \d{4})$/,
      t('wallet.withdraw.error.card_number_format')
    )
    .refine((val) => val.replace(/\s+/g, '').length === 16, {
      message: t('wallet.withdraw.error.card_number_length'),
    }),

  binpay: z.string().optional(),
});
const createDynamicSchema = (t, minAmount, maxAmount, fields) => {
  const validators = fieldValidators(t);

  const schemaDefinition = {
    amount: z
      .number()
      .min(minAmount, {
        message: t('wallet.withdraw.error.min_amount', { min: minAmount }),
      })
      .max(maxAmount, {
        message: t('wallet.withdraw.error.max_amount', { max: maxAmount }),
      }),
  };

  // Перебираем поля с учетом возможной замены 'expire' на пару полей
  fields.forEach((field) => {
    if (validators[field]) {
      schemaDefinition[field] = validators[field];
    } else {
      if (field === 'expire') {
        // Добавляем expireMonth и expireYear вместо expire
        schemaDefinition['expireMonth'] = z
          .string({ message: t('auth.required_field') })
          .refine(
            (val) => {
              return (
                /^\d{2}$/.test(val) && parseInt(val) >= 1 && parseInt(val) <= 12
              );
            },
            {
              message: t('wallet.fiat.input_fields.expire.error_invalid'),
            }
          );

        schemaDefinition['expireYear'] = z
          .string({ message: t('auth.required_field') })
          .refine(
            (val) => {
              const currentYear = new Date().getFullYear() % 100;
              const currentYearFull = new Date().getFullYear();
              const century = Math.floor(currentYearFull / 100) * 100;

              if (!/^\d{2}$/.test(val)) return false;

              const yearValue = parseInt(val);
              const fullYear = century + yearValue;

              return (
                fullYear >= currentYearFull && // Не в прошлом
                fullYear <= currentYearFull + 20
              ); // И не слишком далеко в будущем
            },
            {
              message: t('wallet.fiat.input_fields.expire.error_invalid'),
            }
          );
      } else {
        schemaDefinition[field] = z.string({
          message: t('auth.required_field'),
        });
      }
    }
  });

  // Фильтруем fields - заменяем 'expire' на ['expireMonth', 'expireYear'] если нужно
  const processedFields = fields.flatMap((f) =>
    f === 'expire' ? ['expireMonth', 'expireYear'] : f
  );

  return createSchema(processedFields, schemaDefinition);
};

export {
  withdrawAddressSchema,
  bankCardSchema,
  depositFiatSchema,
  personalInfoSchema,
  fieldValidators,
  createDynamicSchema,
  cardSchema,
  holderCardSchema,
};
